import { DataType, getColor } from './filterconfig'
import { flagData } from '../singleObject'
import TruncatedText from '../truncatedText/TruncatedText'
import moment from 'moment'

// Antd
import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { FlagFilled } from '@ant-design/icons'
import { utförareColumn } from './utforareColumn'
import { ansvarigHandläggareColumn } from './ansvarigHandläggareColumn'
import { hanteringColumn } from './hanteringColumn'
import { useSelector } from 'react-redux'
import { selectTableFilter } from './store/selectors'

const formatDateShort = (date: Date | string) => {
  const d = new Date(date)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const formatDateMonth = (date: Date | string) => {
  return moment(date).format('MMM-YY')
}

export const createSingleTableConfig = (data: DataType[], stateKey: string = 'individ', link?: { from?: string; to?: string; deviation?: string[] }) => {
  // Sortering läses från tableFilterReducer (per individ) — kontrollerad, så valet överlever
  // navigering (#41). Skapad ↓ är bara INITIAL default vid kall ingång (ingen sparad sorter).
  const filterState = useSelector(selectTableFilter)
  const sorter = filterState?.[stateKey]?.sorter
  const sortOrderFor = (key: string) => (sorter?.columnKey === key && sorter?.order ? sorter.order : null)

  const columns: ColumnsType<DataType> = [
    {
      title: 'Flagga',
      dataIndex: 'deviation',
      key: 'deviation',
      sortOrder: sortOrderFor('deviation'),
      showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Typ av flagga' },
      width: 220,
      fixed: 'left',
      sorter: (a, b) => a.flaggnamn.localeCompare(b.flaggnamn),
      render: (_, obj) => (
        <TruncatedText
          text={obj.flaggnamn}
          to={{
            pathname: `/app/avvikelse/flag=${obj.flagga}&id=${obj.key}`,
            state: { flagname: obj.flaggnamn, riskniva: obj.nivå },
          }}
        />
      ),
    },
    {
      title: 'Risknivå',
      dataIndex: 'risk',
      key: 'risk',
      sortOrder: sortOrderFor('risk'),
      showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Flaggans risknivå, dvs. dess allvarlighetsgrad' },
      width: 120,
      sorter: (a, b) => a.nivå - b.nivå,
      render: (_, obj) => (
        <>
          {obj.nivå} <FlagFilled style={{ color: getColor(obj.nivå) }} />
        </>
      ),
    },
    utförareColumn(data, { maxLength: 20, width: 190, filterable: false, link }),
    {
      title: 'Skapad',
      dataIndex: 'created',
      key: 'created',
      width: 95,
      sortOrder: sorter ? sortOrderFor('created') : 'descend',
      sorter: (a, b) => {
        return new Date(a.created).getTime() - new Date(b.created).getTime()
      },
      showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Datum för den körning då flaggan genererades' },
      render: (_, obj) => {
        return <>{formatDateMonth(obj.created)}</>
      },
    },
    {
      title: 'Period',
      dataIndex: 'periodStart',
      key: 'periodStart',
      sortOrder: sortOrderFor('periodStart'),
      showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Tidsperiod som flaggan baserades på' },
      width: 200,
      filterSearch: false,
      sorter: (a, b) => {
        const from = new Date(a.från)
        const to = new Date(b.från)
        return from.getTime() - to.getTime()
      },
      render: (_, obj) => <>{formatDateShort(obj.från)} - {formatDateShort(obj.till)}</>,
    },
    Table.SELECTION_COLUMN,
  ]

  // Visas bara för individuell brukare (data bär fältet); individuell medarbetare saknar det.
  // Placeras efter Utförare, före Skapad — samma relativa plats som i brukare-listan.
  if (data.some((d) => d.ansvarig_handläggare !== undefined)) {
    const skapadIndex = columns.findIndex((c) => (c as { key?: unknown }).key === 'created')
    columns.splice(skapadIndex >= 0 ? skapadIndex : columns.length - 1, 0, ansvarigHandläggareColumn({ sortOrder: sortOrderFor('ansvarig_handläggare') }))
  }

  // Status gäller alla flaggtyper, till skillnad från Ansvarig handläggare. Läggs sist
  // före markeringskolumnen och är fast till höger, se kolumnfilen.
  const selectionIndex = columns.findIndex((c) => c === Table.SELECTION_COLUMN)
  columns.splice(selectionIndex >= 0 ? selectionIndex : columns.length, 0, hanteringColumn({ sortOrder: sortOrderFor('hantering') }))

  return columns
}
